import { FormEvent, useState } from "react";
import { usePageTitle } from "../../Hooks/usePageTitle";
import { User } from "../../Interfaces/User/User";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SignUpPageProps } from "./SignUpProps";
import { auth, db, createUserWithEmailAndPassword, setDoc, doc } from "../../Backend/firebase";
import { applyGrantIfExists } from "../../Services/PlanService/grantCheck";
import { claimMyInvite } from "../../Services/TeamService/teamService";
import { safeRedirect } from "../../utils/safeRedirect";
import { CredentialLoadingScreen } from "../../Components/LoadingScreen/CredentialLoadingScreen";
import "../Login Page/Login.css";
import "./SignUp.css";

export function SignUp({ setUser }: SignUpPageProps): React.JSX.Element {
  usePageTitle("Sign Up");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");
    setAgreeError("");

    if (password.length < 7) {
      setPasswordError("Password must be at least 7 characters");
      return;
    }
    if (!agreed) {
      setAgreeError("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }

    setLoading(true);
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const userRef = doc(db, "users", credential.user.uid);
      // Never store the password — Firebase Auth owns it. Plan fields are
      // written server-side only (webhook / applyGrant); new users are free
      // by default in usePlan when no plan field exists. Record consent to the
      // legal agreements for enforceability.
      await setDoc(userRef, {
        firstName, lastName, email,
        acceptedTermsAt: new Date().toISOString(),
      });
      await applyGrantIfExists(credential.user.uid, email);
      // Auto-join a team if this email was invited
      await claimMyInvite();

      const userData: User = { id: credential.user.uid, firstName, lastName, email, password: "" };
      setUser(userData);
      sessionStorage.setItem("CURRENT_USER", JSON.stringify(userData));
      navigate(safeRedirect(searchParams.get("redirect")));
    } catch (error: any) {
      setLoading(false);
      if (error.code === "auth/email-already-in-use") {
        setEmailError("An account with this email already exists");
      } else {
        setEmailError(error.message);
      }
    }
  }

  if (loading) return <CredentialLoadingScreen />;

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-logo"><span className="auth-logo-live">LIVE</span><span className="auth-logo-cue">CUE</span></div>
        <h1 className="auth-heading">Create account</h1>
        <p className="auth-sub">Get started for free</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field-row">
            <div className="auth-field">
              <label className="auth-label" htmlFor="su-first">First name</label>
              <input
                id="su-first"
                className="auth-input"
                type="text"
                placeholder="First"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="su-last">Last name</label>
              <input
                id="su-last"
                className="auth-input"
                type="text"
                placeholder="Last"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="su-email">Email</label>
            <input
              id="su-email"
              className={`auth-input${emailError ? ' auth-input--error' : ''}`}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(""); }}
              required
              autoComplete="email"
            />
            {emailError && <span className="auth-error">{emailError}</span>}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="su-password">Password</label>
            <input
              id="su-password"
              className={`auth-input${passwordError ? ' auth-input--error' : ''}`}
              type="password"
              placeholder="Min. 7 characters"
              value={password}
              onChange={e => { setPassword(e.target.value); setPasswordError(""); }}
              required
              autoComplete="new-password"
            />
            {passwordError && <span className="auth-error">{passwordError}</span>}
          </div>

          <div className="auth-field">
            <label className="auth-agree">
              <input
                type="checkbox"
                className="auth-agree-box"
                checked={agreed}
                onChange={e => { setAgreed(e.target.checked); setAgreeError(""); }}
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="auth-link">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="auth-link">Privacy Policy</Link>.
              </span>
            </label>
            {agreeError && <span className="auth-error">{agreeError}</span>}
          </div>

          <button className="auth-submit" type="submit">Create account</button>
        </form>

        <p className="auth-footer">
          Already have an account?{' '}
          <Link
            to={
              searchParams.get("redirect")
                ? `/login?redirect=${encodeURIComponent(searchParams.get("redirect")!)}`
                : searchParams.get("plan")
                  ? `/login?plan=${searchParams.get("plan")}`
                  : "/login"
            }
            className="auth-link"
          >Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export default SignUp;
